import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  api,
  ApiError,
  editionReadUrl,
  editionSourceUrl,
  pageImageUrl,
  qs,
  type EditionBrief,
  type EditionCalendar,
  type EditionContentsPage,
  type EditionManifest,
  type PageBrief,
  type PageDetail,
  type UnitType,
} from '../lib/api'
import { AccessNotice, SeatNotice } from '../components/SeatNotice'
import { useSession } from '../lib/session'
import { Chevron, Stat, TypeBadge } from '../components/Page'
import { CountedProgress, EmptyEditions, ErrorCard } from '../components/reader/states'

/**
 * Board 05.1: browse by edition. Pick a date, see the issue, jump to a page.
 *
 * Three rails and a header, and every number on all four is counted from the
 * API rather than asserted: the calendar knows which dates the archive
 * actually holds, the histogram is a real edition count per year, the record
 * under it is the holdings, and the stats row is `editions.stats` as stage 09
 * wrote it. Where a field has not landed yet the line is omitted or set to a
 * plain hyphen; nothing here fills a gap with a plausible figure.
 */

/** A page brief, plus the fields the extended manifest adds as they land. */
interface PageBriefPlus extends PageBrief {
  words?: number | null
}

interface EditionManifestPlus extends Omit<EditionManifest, 'pages'> {
  pages: PageBriefPlus[]
  ingested_at?: string | null
}

/**
 * `gaps` is unspecified in the contract beyond being a list, and the endpoint
 * has since settled on a run of years (`{from, to, years}`). All three shapes
 * a gap could reasonably take are read, so the rail keeps working whichever
 * one the endpoint sends: a run of years, a year with a count of editions
 * missing from it, or a single missing date.
 */
interface CalendarGap {
  year?: number
  missing?: number
  date?: string
  from?: number
  to?: number
  years?: number
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** Parse an ISO date without letting the local time zone move it a day. */
function localDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, (month ?? 1) - 1, day ?? 1)
}

function isoOf(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * The route an edition took. `route` is what ingest recorded; before the
 * extended manifest lands, `source_type` carries the same distinction, because
 * routing is exactly the scan-versus-digital decision.
 */
function routeOf(edition: { route?: 'A' | 'B' | null; source_type?: string }): 'A' | 'B' | null {
  if (edition.route) return edition.route
  if (edition.source_type === 'digital') return 'A'
  if (edition.source_type === 'scan') return 'B'
  return null
}

function pageLabel(page: PageBrief): string {
  return page.section_label ?? String(page.page_number)
}

export function BrowsePage() {
  return <BrowseWorkspace />
}

export function EditionPage() {
  const { editionId } = useParams()
  return <BrowseWorkspace editionId={editionId} />
}

function BrowseWorkspace({ editionId }: { editionId?: string }) {
  const navigate = useNavigate()

  const editions = useQuery({
    queryKey: ['editions', 'browse'],
    queryFn: () => api.get<EditionBrief[]>(`/editions${qs({ limit: 200 })}`),
  })

  // The calendar endpoint is being added; until it lands the rail is built
  // from the edition list, which is the same data more thinly sliced.
  const calendar = useQuery({
    queryKey: ['editions', 'calendar'],
    queryFn: () => api.get<EditionCalendar>('/editions/calendar'),
    retry: false,
  })

  const list = useMemo(
    () =>
      (editions.data ?? [])
        .filter((edition) => !!edition.pub_date)
        .sort((left, right) => (left.pub_date ?? '').localeCompare(right.pub_date ?? '')),
    [editions.data],
  )

  const selectedId = editionId ?? list[list.length - 1]?.id ?? null
  const selected = list.find((edition) => edition.id === selectedId) ?? null

  const manifest = useQuery({
    queryKey: ['edition', selectedId],
    queryFn: () => api.get<EditionManifestPlus>(`/editions/${selectedId}`),
    enabled: !!selectedId,
    retry: false,
  })

  if (editions.isError) {
    return (
      <div className="mx-auto w-full max-w-[560px] p-6">
        <ErrorCard
          title="The list of editions will not load."
          rows={errorRows(editions.error, 'editions')}
          survives="Search, your clippings and your saved searches are unaffected. Only this list is missing."
          actions={[
            { label: 'Try again', onClick: () => void editions.refetch() },
            { label: 'Search instead', href: '/search' },
          ]}
        />
      </div>
    )
  }

  if (editions.isSuccess && list.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[560px] p-6">
        <EmptyEditions
          rows={[
            ['publication', 'none with a completed ingest'],
            ['editions', '0'],
            ['listed', String(editions.data?.length ?? 0)],
          ]}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0 lg:flex-row">
      <CalendarRail
        list={list}
        calendar={calendar.data ?? null}
        selected={selected}
        onPick={(id) => navigate(`/browse/${id}`)}
      />
      <EditionPane
        list={list}
        selected={selected}
        manifest={manifest}
        onPick={(id) => navigate(`/browse/${id}`)}
      />
    </div>
  )
}

// --- the rail --------------------------------------------------------------

function CalendarRail({
  list,
  calendar,
  selected,
  onPick,
}: {
  list: EditionBrief[]
  calendar: EditionCalendar | null
  selected: EditionBrief | null
  onPick: (id: string) => void
}) {
  const byDate = useMemo(() => {
    const map = new Map<string, EditionBrief>()
    for (const edition of list) if (edition.pub_date) map.set(edition.pub_date, edition)
    return map
  }, [list])

  /** Dates the archive holds, from the calendar when it exists and the list otherwise. */
  const held = useMemo(() => {
    const dates = new Set<string>()
    if (calendar?.years?.length) {
      for (const year of calendar.years) {
        for (const days of Object.values(year.months ?? {})) {
          for (const day of days) dates.add(day)
        }
      }
    }
    for (const date of byDate.keys()) dates.add(date)
    return dates
  }, [calendar, byDate])

  const gaps = useMemo(() => {
    const rows = (calendar?.gaps ?? []) as CalendarGap[]
    const dates = new Set<string>()
    const years = new Set<number>()
    let editions = 0
    let spanYears = 0
    for (const gap of rows) {
      if (gap.date) {
        dates.add(gap.date)
        years.add(Number(gap.date.slice(0, 4)))
        editions += 1
      }
      if (gap.year !== undefined) {
        years.add(gap.year)
        editions += gap.missing ?? 0
      }
      if (gap.from !== undefined && gap.to !== undefined) {
        for (let value = gap.from; value <= gap.to; value += 1) years.add(value)
        spanYears += gap.years ?? gap.to - gap.from + 1
      }
    }
    return { dates, years, editions, spanYears }
  }, [calendar])

  const anchor = selected?.pub_date ?? [...held].sort().pop() ?? null
  const [month, setMonth] = useState(() => (anchor ? localDate(anchor) : new Date()))
  useEffect(() => {
    if (anchor) setMonth(localDate(anchor))
  }, [anchor])

  const year = month.getFullYear()
  const monthIndex = month.getMonth()
  const first = new Date(year, monthIndex, 1)
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  // The grid runs Monday to Sunday, so Sunday's index 0 becomes 6.
  const lead = (first.getDay() + 6) % 7

  const years = useMemo(() => {
    const counts = new Map<number, number>()
    if (calendar?.years?.length) {
      for (const entry of calendar.years) counts.set(entry.year, entry.editions)
    } else {
      for (const date of held) {
        const value = Number(date.slice(0, 4))
        counts.set(value, (counts.get(value) ?? 0) + 1)
      }
    }
    if (counts.size === 0) return { rows: [], total: 0, first: null, last: null }
    const keys = [...counts.keys()].sort((left, right) => left - right)
    const firstYear = keys[0]
    const lastYear = keys[keys.length - 1]
    const rows: { year: number; editions: number }[] = []
    for (let value = firstYear; value <= lastYear; value += 1) {
      rows.push({ year: value, editions: counts.get(value) ?? 0 })
    }
    const total = rows.reduce((sum, row) => sum + row.editions, 0)
    return { rows, total, first: firstYear, last: lastYear }
  }, [calendar, held])

  const peak = Math.max(1, ...years.rows.map((row) => row.editions))

  /** The scanned and digital spans, counted rather than asserted. */
  const spans = useMemo(() => {
    const out: Record<'A' | 'B', { from: number; to: number } | null> = { A: null, B: null }
    for (const edition of list) {
      const route = routeOf(edition)
      if (!route || !edition.pub_date) continue
      const value = Number(edition.pub_date.slice(0, 4))
      const current = out[route]
      out[route] = current
        ? { from: Math.min(current.from, value), to: Math.max(current.to, value) }
        : { from: value, to: value }
    }
    return out
  }, [list])

  const jumpToYear = (value: number) => {
    const match = [...held]
      .filter((date) => date.startsWith(String(value)))
      .sort()
    // A year the archive holds nothing for is still worth opening: that is
    // what "see gaps" means, and the month grid is where a gap is legible.
    if (match.length === 0) {
      setMonth(new Date(value, 0, 1))
      return
    }
    const edition = byDate.get(match[0])
    if (edition) onPick(edition.id)
    else setMonth(localDate(match[0]))
  }

  return (
    <aside className="w-full flex-none border-b border-n300 bg-surface px-4 py-4 lg:w-[300px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="chrome">Pick a date</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-ghost px-1 text-ink2"
            aria-label="Previous month"
            onClick={() => setMonth(new Date(year, monthIndex - 1, 1))}
          >
            <Chevron direction="left" size={11} />
          </button>
          <div className="font-mono text-[11px]">
            {MONTH_NAMES[monthIndex]} {year}
          </div>
          <button
            type="button"
            className="btn-ghost px-1 text-ink2"
            aria-label="Next month"
            onClick={() => setMonth(new Date(year, monthIndex + 1, 1))}
          >
            <Chevron direction="right" size={11} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((letter, index) => (
          <div
            key={`${letter}${index}`}
            className="pb-1 text-center font-mono text-[9.5px] text-ink2"
          >
            {letter}
          </div>
        ))}
        {Array.from({ length: lead }, (_, index) => (
          <div key={`lead${index}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1
          const iso = isoOf(year, monthIndex + 1, day)
          const edition = byDate.get(iso)
          const exists = held.has(iso)
          // A date the archive knows is missing, either because the calendar
          // named it or because it falls inside a run of years the calendar
          // reports as holding nothing. Not held and known missing are
          // different claims, and only the second one is marked.
          const gap = gaps.dates.has(iso) || (!exists && gaps.years.has(year))
          const chosen = selected?.pub_date === iso
          const className = [
            'flex aspect-square items-center justify-center rounded-[--radius-md] font-mono text-[11px]',
            chosen ? 'text-paper' : exists ? 'text-body' : 'text-ink4 line-through',
            edition ? 'cursor-pointer' : 'cursor-default',
          ].join(' ')
          return (
            <button
              key={iso}
              type="button"
              disabled={!edition}
              onClick={() => edition && onPick(edition.id)}
              aria-current={chosen ? 'date' : undefined}
              title={
                edition
                  ? `${WEEKDAYS[localDate(iso).getDay()]} ${iso}`
                  : gap
                    ? `${iso}: missing from the archive`
                    : `${iso}: no edition held`
              }
              className={className}
              style={{
                background: chosen ? 'var(--jl-accent)' : 'transparent',
                // Dashed rather than solid: a whole month of missing dates
                // has to read as absence, not as thirty-one alarms.
                border: gap
                  ? '1px dashed var(--color-accent-2-700)'
                  : '1px solid transparent',
              }}
            >
              {day}
            </button>
          )
        })}
      </div>

      <div className="mt-4 border-t border-n300 pt-3.5">
        <div className="chrome mb-2.5">Jump by year</div>
        <div className="flex h-12 items-end gap-px">
          {years.rows.map((row) => {
            const empty = row.editions === 0
            const height = empty ? 6 : 24 + (row.editions / peak) * 76
            const current = selected?.pub_date?.startsWith(String(row.year))
            return (
              <button
                key={row.year}
                type="button"
                aria-label={`${row.year}: ${row.editions} editions`}
                title={`${row.year} · ${row.editions} edition${row.editions === 1 ? '' : 's'}`}
                onClick={() => jumpToYear(row.year)}
                className="flex-1"
                style={{
                  height: `${height}%`,
                  // Magenta is the mark for an edition the archive knows is
                  // missing. A year the archive simply does not cover is not
                  // in doubt, so it recedes into the neutral instead.
                  background: gaps.years.has(row.year)
                    ? 'var(--color-accent-2-700)'
                    : empty
                      ? 'var(--color-neutral-400)'
                      : 'var(--jl-accent)',
                  opacity: empty ? 1 : current ? 1 : 0.42,
                }}
              />
            )
          })}
        </div>
        <div className="mt-1.5 flex justify-between font-mono text-[9.5px] text-ink2">
          <div>{years.first ?? '-'}</div>
          <div>
            {years.first && years.last
              ? Math.round((years.first + years.last) / 2)
              : '-'}
          </div>
          <div>{years.last ?? '-'}</div>
        </div>
      </div>

      <div className="mt-4 border-t border-n300 pt-3.5 font-mono text-[10px] uppercase leading-[1.8] text-ink2">
        <div className="text-body">
          {years.total.toLocaleString()} edition{years.total === 1 ? '' : 's'} held
        </div>
        {spans.B && (
          <div>
            {spans.B.from}&ndash;{spans.B.to} scanned · route B
          </div>
        )}
        {spans.A && (
          <div>
            {spans.A.from}&ndash;{spans.A.to} digital · route A
          </div>
        )}
        {/*
          The endpoint reports what it can count. Where it counts editions the
          line says editions; where it reports a run of years with nothing in
          it, the line says years. It never converts one into the other.
        */}
        {gaps.editions > 0 && (
          <div className="text-mark">
            {gaps.editions.toLocaleString()} edition{gaps.editions === 1 ? '' : 's'} missing · see
            gaps
          </div>
        )}
        {gaps.editions === 0 && gaps.spanYears > 0 && (
          <div className="text-mark">
            {gaps.spanYears.toLocaleString()} year{gaps.spanYears === 1 ? '' : 's'} with no
            editions held
          </div>
        )}
      </div>
    </aside>
  )
}

// --- the issue -------------------------------------------------------------

function EditionPane({
  list,
  selected,
  manifest,
  onPick,
}: {
  list: EditionBrief[]
  selected: EditionBrief | null
  manifest: ReturnType<typeof useQuery<EditionManifestPlus>> | ReturnType<typeof useQuery>
  onPick: (id: string) => void
}) {
  const { can } = useSession()
  const query = manifest as {
    data?: EditionManifestPlus
    error: unknown
    isError: boolean
    isLoading: boolean
    refetch: () => void
  }
  const data = query.data
  const [loadedThumbs, setLoadedThumbs] = useState(0)

  useEffect(() => {
    setLoadedThumbs(0)
  }, [data?.id])

  const pages = useMemo(() => data?.pages ?? [], [data?.pages])
  const derived = useMemo(() => derivedUnits(pages), [pages])

  if (query.isError) {
    const error = query.error
    const status = error instanceof ApiError ? error.status : null
    if (status === 429) {
      return (
        <div className="flex-1">
          <SeatNotice error={error} onRetry={() => query.refetch()} />
        </div>
      )
    }
    if (status === 403 || status === 401) {
      return (
        <div className="flex-1">
          <AccessNotice error={error} asked={selected?.pub_date ?? null} />
        </div>
      )
    }
    return (
      <div className="mx-auto w-full max-w-[560px] flex-1 p-6">
        <ErrorCard
          title="This issue will not load."
          rows={errorRows(error, 'edition', selected?.pub_date ?? null)}
          survives="The record, the coordinates and the recognised text are all intact and readable. Only this manifest is missing."
          actions={[
            { label: 'Try again', onClick: () => query.refetch() },
            { label: 'Search instead', href: '/search' },
          ]}
        />
      </div>
    )
  }

  const index = selected ? list.findIndex((edition) => edition.id === selected.id) : -1
  const previous = index > 0 ? list[index - 1] : null
  const next = index >= 0 && index < list.length - 1 ? list[index + 1] : null

  const sameDay = selected?.pub_date
    ? list.filter((edition) => edition.pub_date === selected.pub_date)
    : []
  const ordinalOfDay = selected ? sameDay.findIndex((edition) => edition.id === selected.id) + 1 : 0
  const route = data ? routeOf(data) : selected ? routeOf(selected) : null
  const weekday = selected?.pub_date ? WEEKDAYS[localDate(selected.pub_date).getDay()] : null
  const firstPage = pages[0] ?? null

  const stats = data?.stats ?? {}
  /**
   * `editions.stats` is written by stage 09. An edition that has not reached
   * it comes back with every count at zero and a null mean, which means "not
   * counted" rather than "none": printing 0 articles over an edition that
   * holds 349 of them would be a lie the interface can easily avoid. So the
   * stats block is used only when it was actually written, and the header says
   * plainly when it was not.
   */
  const indexed =
    stats.mean_confidence !== null && stats.mean_confidence !== undefined
      ? true
      : (stats.words ?? 0) > 0

  return (
    <div className="flex min-w-0 flex-1 flex-col lg:min-h-0">
      <header className="flex-none border-b border-n300 px-5 pb-3.5 pt-4">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="mb-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink2">
              {[
                weekday,
                sameDay.length > 0
                  ? `edition ${ordinalOfDay} of ${sameDay.length}`
                  : null,
                route ? `route ${route}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <h1 className="text-[32px] leading-[1.04]">
              {selected?.pub_date ? longDate(selected.pub_date) : 'No edition selected'}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-label="Previous edition"
              disabled={!previous}
              onClick={() => previous && onPick(previous.id)}
              className="flex h-8 w-8 items-center justify-center rounded-[--radius-md] border border-rule2 disabled:opacity-40"
            >
              <Chevron direction="left" />
            </button>
            <button
              type="button"
              aria-label="Next edition"
              disabled={!next}
              onClick={() => next && onPick(next.id)}
              className="flex h-8 w-8 items-center justify-center rounded-[--radius-md] border border-rule2 disabled:opacity-40"
            >
              <Chevron direction="right" />
            </button>
            <Link
              to={`/compare${qs({ left: previous?.id ?? '', right: selected?.id ?? '' })}`}
              className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal no-underline"
            >
              Compare editions
            </Link>
            {selected && can('content.download') && (
              <a
                href={editionSourceUrl(selected.id)}
                className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal no-underline"
                download
              >
                Download PDF
              </a>
            )}
            {firstPage && (
              <Link
                to={`/page/${firstPage.id}`}
                className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal no-underline"
              >
                Open page {pageLabel(firstPage)}
              </Link>
            )}
            {selected && can('content.view_page_image') && (
              <a
                href={editionReadUrl(selected.id)}
                target="_blank"
                rel="noopener"
                className="btn-primary px-[13px] py-1.5 text-[13.5px] no-underline"
              >
                Read the issue (PDF)
              </a>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-[26px] gap-y-2 border-t border-n200 pt-3">
          <Stat label="Pages" value={fmt(stats.pages ?? data?.page_count ?? pages.length)} />
          <Stat
            label="Articles"
            value={fmt(indexed ? stats.articles : (selected?.article_count ?? derived.article))}
          />
          <Stat
            label="Photographs"
            value={fmt(indexed ? stats.photographs : derived.photograph)}
          />
          <Stat
            label="Advertisements"
            value={fmt(indexed ? stats.advertisements : derived.advertisement)}
          />
          <Stat label="Notices" value={fmt(indexed ? stats.notices : derived.notice)} />
          <Stat
            label="Mean confidence"
            value={
              indexed && stats.mean_confidence
                ? `${(stats.mean_confidence * 100).toFixed(1)}%`
                : '-'
            }
            tone={
              indexed && stats.mean_confidence && stats.mean_confidence < 0.85 ? 'mark' : undefined
            }
          />
          <Stat label="Ingested" value={data?.ingested_at?.slice(0, 16).replace('T', ' ') ?? '-'} />
        </div>
        {data && !indexed && (
          <div className="mt-2 font-mono text-[9.5px] uppercase leading-[1.7] text-mark">
            Stage 09 has not indexed this edition, so it has no counts of its own. The figures
            above are from the page manifest, and the pages themselves are unaffected.
          </div>
        )}
      </header>

      <div className="flex flex-1 flex-col xl:flex-row xl:min-h-0">
        <div className="min-w-0 flex-1 px-5 py-4 lg:overflow-y-auto">
          <div className="mb-3 flex items-baseline justify-between gap-4">
            <div className="chrome">Pages · {pages.length || fmt(data?.page_count)}</div>
          </div>

          {query.isLoading && (
            <CountedProgress
              percent={0}
              label={`0 of ${selected?.page_count ?? 0} pages`}
              right="manifest"
            />
          )}

          {pages.length > 0 && loadedThumbs < pages.length && (
            <CountedProgress
              percent={(loadedThumbs / pages.length) * 100}
              label={`${loadedThumbs} of ${pages.length} page images`}
              right={data?.pages[0]?.ppi ? `${data.pages[0].ppi} ppi` : undefined}
            />
          )}

          <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-3.5">
            {pages.map((page, position) => (
              <PageTile
                key={page.id}
                page={page}
                first={position === 0}
                date={selected?.pub_date ?? null}
                onSettled={() => setLoadedThumbs((value) => value + 1)}
              />
            ))}
          </div>
        </div>

        <ContentsRail
          editionId={data?.id ?? null}
          contents={data?.contents ?? null}
          pages={pages}
        />
      </div>
    </div>
  )
}

/** "18 August 2026", the way the board's edition header sets a date. */
function longDate(iso: string): string {
  const date = localDate(iso)
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`
}

function fmt(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : value.toLocaleString()
}

/** Sum the per-page unit counts the extended manifest carries, when it does. */
function derivedUnits(pages: PageBriefPlus[]): Partial<Record<UnitType, number>> {
  const out: Partial<Record<UnitType, number>> = {}
  let any = false
  for (const page of pages) {
    if (!page.units) continue
    any = true
    for (const [type, count] of Object.entries(page.units)) {
      const key = type as UnitType
      out[key] = (out[key] ?? 0) + (count ?? 0)
    }
  }
  return any ? out : {}
}

/**
 * A page thumbnail. A page image needs a licence, so a guest gets a 403 here
 * and a bare `<img>` would draw the browser's broken-image glyph: that says
 * the archive is broken when the page is there and the reader is not entitled
 * to it. The hatched placeholder names what belongs in the space instead, and
 * it sits on the well because the space it stands in is the well.
 */
function PageTile({
  page,
  first,
  date,
  onSettled,
}: {
  page: PageBriefPlus
  first: boolean
  date: string | null
  onSettled: () => void
}) {
  const [failed, setFailed] = useState(false)
  const label = pageLabel(page)
  const units = page.units
    ? Object.values(page.units).reduce((sum, count) => sum + (count ?? 0), 0)
    : null
  const right =
    page.words !== null && page.words !== undefined
      ? `${page.words.toLocaleString()}w`
      : units !== null
        ? `${units}u`
        : '-'

  return (
    <Link to={`/page/${page.id}`} className="flex flex-col gap-[5px] no-underline">
      <div
        className="bg-well p-[3px]"
        style={{ boxShadow: first ? '0 0 0 2px var(--jl-accent)' : 'none' }}
      >
        {failed || page.withdrawn ? (
          <div
            className="flex aspect-[400/818] w-full items-center justify-center text-center"
            style={{
              background: 'repeating-linear-gradient(135deg,#3a3735 0 3px,#252321 3px 6px)',
            }}
          >
            <div className="font-mono text-[9px] uppercase leading-snug text-ink4">
              <div>page {label}</div>
              <div>{page.withdrawn ? 'withdrawn' : 'no image'}</div>
            </div>
          </div>
        ) : (
          <img
            src={page.thumbnail_url ?? pageImageUrl(page.id, true)}
            alt={`Page ${label}${date ? ` of the ${date} edition` : ''}`}
            loading="lazy"
            className="block w-full"
            onLoad={onSettled}
            onError={() => {
              setFailed(true)
              onSettled()
            }}
          />
        )}
      </div>
      <div className="flex items-baseline justify-between">
        <div className={`font-mono text-[10.5px] ${first ? 'text-body' : 'text-ink2'}`}>{label}</div>
        <div className="font-mono text-[9px] text-ink2">{right}</div>
      </div>
    </Link>
  )
}

// --- contents of this issue ------------------------------------------------

/**
 * The issue contents.
 *
 * `contents` on the manifest is the endpoint's own answer. Until it lands the
 * rail is built from `GET /api/pages/{id}`, which already returns each page's
 * units: the same real data, fetched a page at a time and capped, rather than
 * a placeholder list.
 */
function ContentsRail({
  editionId,
  contents,
  pages,
}: {
  editionId: string | null
  contents: EditionContentsPage[] | null
  pages: PageBriefPlus[]
}) {
  const needsFallback = !!editionId && !contents
  const capped = needsFallback ? pages.slice(0, 12) : []

  const details = useQueries({
    queries: capped.map((page) => ({
      queryKey: ['page', page.id, 'units'],
      queryFn: () => api.get<PageDetail>(`/pages/${page.id}`),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  })

  const rows: EditionContentsPage[] = useMemo(() => {
    if (contents) return contents
    return capped
      .map((page, index): EditionContentsPage | null => {
        const detail = details[index]?.data as PageDetail | undefined
        if (!detail?.units?.length) return null
        return {
          page_id: page.id,
          page_number: page.page_number,
          section_label: detail.section_label ?? page.section_label,
          units: detail.units.map((unit) => ({
            id: unit.id,
            unit_type: unit.unit_type,
            title: unit.title,
          })),
        }
      })
      .filter((row): row is EditionContentsPage => row !== null)
    // `details` is a fresh array each render; its data is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contents, capped, details.map((entry) => entry.dataUpdatedAt).join(',')])

  const loading = needsFallback && details.some((entry) => entry.isLoading)

  return (
    <aside className="w-full flex-none border-t border-n300 px-[18px] py-4 lg:overflow-y-auto xl:w-[330px] xl:border-l xl:border-t-0">
      <div className="chrome mb-3">Contents of this issue</div>
      {loading && (
        <CountedProgress
          percent={
            (details.filter((entry) => !entry.isLoading).length / Math.max(1, details.length)) * 100
          }
          label={`${details.filter((entry) => !entry.isLoading).length} of ${details.length} pages read`}
        />
      )}
      {!loading && rows.length === 0 && (
        <p className="text-[14px] leading-normal text-ink2">
          This edition has not been indexed, so it has no contents list yet. Stage 09 writes one;
          the pages, their images and their coordinates are all here and unaffected.
        </p>
      )}
      <div className="flex flex-col">
        {rows.map((row) => (
          <div key={row.page_id} className="mb-3 border-b border-n200 pb-3">
            <div className="mb-[7px] flex items-baseline gap-2">
              <div className="font-mono text-[11.5px] text-body">
                {row.section_label ?? (row.page_number ? `p${row.page_number}` : '-')}
              </div>
              {row.section_label && (
                <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
                  {sectionName(row.section_label)}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {row.units.map((unit) => (
                <div key={unit.id} className="flex items-start gap-2">
                  <span className="mt-0.5 flex-none">
                    <TypeBadge type={unit.unit_type} />
                  </span>
                  {/*
                    A unit's title is whatever the assembly gave it, and on a
                    worn scan that can be a whole column of recognised text.
                    The rail shows the opening of it and carries the rest in
                    the tooltip rather than truncating the record itself.
                  */}
                  <Link
                    to={`/unit/${unit.id}`}
                    title={unit.title ?? undefined}
                    className="line-clamp-2 text-[14.5px] leading-[1.35] text-body no-underline hover:text-accent"
                  >
                    {unit.title || 'Untitled'}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {needsFallback && pages.length > capped.length && (
        <div className="font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
          First {capped.length} of {pages.length} pages · the issue manifest will carry the rest
        </div>
      )}
    </aside>
  )
}

/**
 * The section a label belongs to. A section label is a per-publication
 * convention, so the letter is all the interface may read from it; the name is
 * the publication's own and is left blank rather than guessed.
 */
function sectionName(label: string): string {
  const letter = /^([A-Za-z]+)/.exec(label)?.[1]
  return letter ? `section ${letter.toUpperCase()}` : ''
}

/** An error names the stage that failed, the reason and the time. */
function errorRows(error: unknown, stage: string, subject?: string | null): [string, string][] {
  const status = error instanceof ApiError ? error.status : null
  const detail = error instanceof Error ? error.message : String(error)
  return [
    ['what', subject ? `${stage} · ${subject}` : stage],
    ['stage', status ? `api ${stage} · ${status}` : `api ${stage} · error`],
    ['reason', detail],
    ['at', new Date().toISOString().slice(0, 19).replace('T', ' ')],
  ]
}
